// 製作尺寸：整個專案的 LED 畫布等比縮小，動畫師拿到的所有尺寸（對位圖、位置表、AE 合成腳本、版型、測試圖、交片檢查）跟著變。
// 判斷規則在 lib/workScale.ts（前後台共用）；這裡只負責列出所有可以選的尺寸（用 % 顯示）、常見比例為什麼不行，和切換前的確認。
import { useEffect, useMemo, useState } from 'react';
import { CircleCheck, TriangleAlert, X } from 'lucide-react';
import Icon from './Icon';
import { confirmDialog, toast } from './dialogs';
import {
  oddOriginals, sameWorkScale, scaleCanvasSize, workScalePickList, workScaleText, type WorkScale, type WorkScaleInput, type WorkScaleOption,
} from '../lib/workScale';
import './WorkScaleDialog.css';

interface Props {
  open: boolean;
  onClose: () => void;
  /** 原尺寸的畫布、每塊 LED 的位置、版型（都是原尺寸） */
  input: WorkScaleInput | null;
  /** LED 的位置有沒有算出來（模型還沒載好 = false：只檢查畫布和版型） */
  hasPanels: boolean;
  current: WorkScale | null;
  /** 改比例；成功回 null，失敗回後台的說明 */
  onChange: (v: WorkScale | null) => Promise<string | null>;
}

const dims = (c: { width: number; height: number }) => `${c.width} × ${c.height}`;

export default function WorkScaleDialog({ open, onClose, input, hasPanels, current, onChange }: Props) {
  const [busyKey, setBusyKey] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const pick = useMemo(() => (input ? workScalePickList(input) : null), [input]);
  const options = pick?.options ?? [];
  const odd = useMemo(() => (input ? oddOriginals(input) : []), [input]);

  if (!open) return null;

  async function choose(o: WorkScaleOption) {
    if (!o.ok || busyKey || sameWorkScale(o.scale, current)) return;
    const full = o.scale.num === o.scale.den;
    const ok = await confirmDialog({
      title: full ? '切回原尺寸？' : `製作尺寸改成 ${o.text}？`,
      message: `畫布會變成 ${dims(o.canvas)}。\n動畫師手上的 AE 合成不會自動變，要重新發打包檔，他們才會拿到新尺寸。`,
      confirmLabel: full ? '切回原尺寸' : '改成這個尺寸',
    });
    if (!ok) return;
    setBusyKey(o.text);
    const err = await onChange(full ? null : o.scale);
    setBusyKey(null);
    if (err) toast.error(err);
    else onClose();
  }

  return (
    <div className="wsd-overlay" onClick={onClose}>
      <div className="wsd" role="dialog" aria-label="製作尺寸" onClick={(e) => e.stopPropagation()}>
        <header className="wsd__header">
          <div>
            <h2>製作尺寸</h2>
            <p className="wsd__sub muted">
              畫布太大時動畫師做不動，可以把整個專案等比縮小，動畫師交縮小版，現場再放大。原尺寸的數字都留著，隨時可以切回來。
            </p>
          </div>
          <button className="wsd__close" onClick={onClose} title="關閉" aria-label="關閉"><Icon icon={X} size="md" /></button>
        </header>
        <div className="wsd__body">
          {!input ? (
            <div className="wsd__empty muted">要先設定 LED 畫布大小，才能選製作尺寸。</div>
          ) : (
            <>
              <div className="wsd__facts">
                <div>原尺寸：<strong>{dims(input.canvas)}</strong></div>
                <div>目前：<strong>{workScaleText(current)}</strong>{current ? `（${dims(scaleCanvasSize(input.canvas, current))}）` : ''}</div>
              </div>
              {!hasPanels && (
                <div className="wsd__note">模型還沒載好，這裡只檢查了畫布和版型；每塊 LED 的位置等模型載好後再開一次才會檢查。</div>
              )}
              {odd.length > 0 && (
                <div className="wsd__note wsd__note--warn" data-testid="wsd-odd">
                  <Icon icon={TriangleAlert} size="sm" />
                  <span>
                    原尺寸本身就有單數：{odd.slice(0, 4).map(p => p.text).join('；')}{odd.length > 4 ? `…等 ${odd.length} 處` : ''}。原尺寸照常能用，但現場 mapping 收到單數比較難處理。
                  </span>
                </div>
              )}
              <p className="wsd__count muted">{options.length > 1 ? `有 ${options.length - 1} 個尺寸可以選${pick?.thinned ? '（太多了，這裡平均列出一部分）' : ''}，由大到小。` : '除了原尺寸，沒有其他尺寸符合條件。'}</p>
              <ul className="wsd__list wsd__list--scroll">
                {options.map(o => {
                  const active = sameWorkScale(o.scale, current);
                  return (
                    <li key={`${o.scale.num}/${o.scale.den}`} className={'wsd__row' + (active ? ' is-active' : '')} data-testid={`wsd-${o.scale.num}-${o.scale.den}`}>
                      <button
                        type="button"
                        className="wsd__pick"
                        disabled={!!busyKey}
                        aria-pressed={active}
                        onClick={() => { void choose(o); }}
                      >
                        <span className="wsd__name">{o.text}</span>
                        <span className="wsd__size">{dims(o.canvas)}</span>
                        {active && <span className="wsd__tag"><Icon icon={CircleCheck} size="sm" /> 目前</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
              {pick && pick.blocked.length > 0 && (
                <details className="wsd__blocked">
                  <summary>常見比例為什麼不行（{pick.blocked.length}）</summary>
                  <ul>{pick.blocked.map(o => <li key={o.text} data-testid={`wsd-blocked-${o.scale.num}-${o.scale.den}`}>{o.reason}</li>)}</ul>
                </details>
              )}
              <p className="wsd__foot muted">
                可以選的條件：縮完以後畫布的寬高、每一塊 LED 的 x、y、寬、高、每個版型的畫布和每塊螢幕的位置大小，全部都是整數雙數。
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
